import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  MapPin, Clock, CheckCircle2, LogIn, LogOut, Loader2, AlertCircle,
  CalendarClock, ShieldCheck, XCircle, CalendarX2, Navigation, HelpCircle,
  Hourglass, ShieldQuestion, ChevronDown, ChevronUp, Scissors,
  TrendingUp, CalendarDays, ChevronLeft, ChevronRight, Users, Store, CalendarRange,
  Trophy, Target, Award, Flame, Zap, Medal, BarChart3, Rocket, Star, Crown, Lock,
  Sparkles, Bell, X, Phone, MessageCircle, CalendarPlus, Send, UserRound, LifeBuoy,
  Wallet, Euro, CheckCircle, Receipt, Eye,
} from "lucide-react";
import { SpotInstructions, SpotAuthorizedBy } from "@/components/SpotInstructions";
import { MaPaie } from "@/components/MaPaie";
import { RecruiterAvatar, rdPhotoSrc } from "@/components/RecruiterAvatar";
import { SpotProofButton } from "@/components/SpotProofDialog";
import { AnnouncementModal } from "@/components/AnnouncementModal";
import { CollabQualite } from "@/components/pointage/qualite";

import {
  PerfStrip, CollabDashboard, StreakReminderBanner, RatioAlertBanner,
  RatioGreenBanner, WeeklyRecapBanner, LevelUpCelebration,
  usePerfCelebrations,
} from "@/components/pointage/perf";

const fmtH = (h) => (h === null || h === undefined ? "—" : Number(h).toFixed(2).replace(".", ","));
const frDay = (iso) => (iso ? `${iso.slice(8)}/${iso.slice(5, 7)}` : "—");
const FR_MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const frMonth = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  return `${FR_MONTHS[parseInt(m, 10) - 1] || m} ${y}`;
};

const STATUT_LABEL = {
  pointe: "Pointé",
  en_mission: "En mission — départ à pointer",
  deduit_a_confirmer: "Déduit — à confirmer",
  corrige_admin: "Corrigé",
  saisie_admin_retroactive: "Saisie admin",
  annule: "Annulé",
  absence_a_trancher: "Absence à trancher",
  anomalie: "Anomalie",
  exceptionnel_en_attente: "Exceptionnel — en attente de validation",
  exceptionnel_valide: "Exceptionnel — validé",
  exceptionnel_rejete: "Exceptionnel — refusé (heures non comptées)",
};

const STATUT_CLS = {
  pointe: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]",
  en_mission: "bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]",
  corrige_admin: "bg-[rgba(185,137,0,0.14)] text-[#8a6600]",
  saisie_admin_retroactive: "bg-[rgba(93,63,211,0.12)] text-[#5D3FD3]",
  annule: "bg-[rgba(214,69,69,0.10)] text-[#9B2226]",
  exceptionnel_en_attente: "bg-[rgba(185,137,0,0.14)] text-[#8a6600]",
  exceptionnel_rejete: "bg-[rgba(214,69,69,0.10)] text-[#9B2226]",
};

const BLOCK_TITLE = {
  hors_zone: "Vous êtes trop loin du spot",
  gps_indisponible: "Position GPS indisponible",
  gps_refuse: "Localisation désactivée",
  spot_sans_gps: "Spot sans coordonnées GPS",
};

// Motif court d'un pointage exceptionnel (mention de transparence côté collaborateur).
const MOTIF_LABEL = {
  hors_zone: "Hors zone GPS",
  gps_indisponible: "GPS indisponible",
  gps_refuse: "Localisation refusée",
  spot_sans_gps: "Spot sans GPS",
};

function getPosition() {
  return new Promise((resolve) => {
    if (!("geolocation" in navigator)) return resolve(null);
    let done = false;
    const t = setTimeout(() => { if (!done) { done = true; resolve(null); } }, 6000);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (!done) {
          done = true; clearTimeout(t);
          resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        }
      },
      () => { if (!done) { done = true; clearTimeout(t); resolve(null); } },
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 0 }
    );
  });
}

async function postPointerWithRetry(token, body, attempts = 2) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await publicApi.post(`/pointage/public/${token}/pointer`, body, { timeout: 12000 });
    } catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      const transient = !e?.response || status >= 500;
      if (!transient || i === attempts - 1) break;
      await new Promise((r) => setTimeout(r, 900));
    }
  }
  throw lastErr;
}

export default function Pointage() {
  const { token } = useParams();
  const [boot, setBoot] = useState(null);
  const [bootErr, setBootErr] = useState(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [tab, setTab] = useState("pointage");
  const [stats, setStats] = useState(null);   // stats gamifiées (bandeau + onglet perf)
  const [rebookAlerts, setRebookAlerts] = useState([]);  // spots suivis désormais libres (Phase 26)
  const [paie, setPaie] = useState(null);     // relevés de paie (onglet « Ma paie » — conditionnel)
  const { levelUp, closeLevelUp, showReminder, dismissReminder, showRatioAlert, dismissRatioAlert,
    showRatioGreen, dismissRatioGreen, showRecap, dismissRecap } = usePerfCelebrations(token, stats);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await publicApi.get(`/pointage/public/${token}/espace`);
        if (!alive) return;
        setBoot(r.data);
        setTab(r.data.can_pointer ? "pointage" : "planning");
      } catch (e) {
        if (!alive) return;
        setBootErr({ code: e?.response?.status, message: e?.response?.data?.detail || "Lien indisponible." });
      } finally {
        if (alive) setBootLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  // Stats gamifiées chargées une seule fois (au niveau du shell) pour alimenter le
  // bandeau TOUJOURS visible ET l'onglet « Ma perf » (évite un double appel).
  useEffect(() => {
    if (!boot) return;
    let alive = true;
    publicApi.get(`/pointage/public/${token}/mes-stats`)
      .then((r) => { if (alive) setStats(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [boot, token]);

  // Alerte libération (Phase 26) : spots suivis (« Prévenez-moi ») désormais LIBRES.
  useEffect(() => {
    if (!boot?.rebook_allowed) return;
    let alive = true;
    publicApi.get(`/pointage/public/${token}/rebook/alerts`)
      .then((r) => { if (alive) setRebookAlerts(r.data?.alerts || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [boot, token]);
  const dismissAlert = async (spotId) => {
    setRebookAlerts((a) => a.filter((x) => x.spot_id !== spotId));
    try { await publicApi.post(`/pointage/public/${token}/rebook/alerts/dismiss`, { spot_id: spotId }); }
    catch { /* silencieux : l'alerte est déjà masquée localement */ }
  };

  // Relevés de paie (Reporting). RÉSERVÉ AUX INDÉPENDANTS ; l'onglet « Ma paie »
  // n'apparaît QUE si l'indé est autorisé ET sa paie publiée (visible:true + releves non vide).
  useEffect(() => {
    if (!boot || !boot.is_independant) return;
    let alive = true;
    publicApi.get(`/pointage/public/${token}/ma-paie`)
      .then((r) => { if (alive) setPaie(r.data); })
      .catch(() => { if (alive) setPaie(null); });
    return () => { alive = false; };
  }, [boot, token]);
  const showPaie = !!(paie && paie.visible && Array.isArray(paie.releves) && paie.releves.length > 0);

  // Pastille « nouveau relevé » sur l'onglet « Ma paie » : on compare la date de
  // publication la plus récente à ce que le collaborateur a déjà vu (mémorisé en
  // localStorage, par lien). La pastille disparaît dès qu'il ouvre l'onglet.
  const latestPaiePub = showPaie
    ? paie.releves.reduce((m, r) => (r.published_at && (!m || String(r.published_at) > String(m)) ? r.published_at : m), null)
    : null;
  const [paieSeen, setPaieSeen] = useState(null);
  useEffect(() => {
    try { setPaieSeen(localStorage.getItem(`shariti_paie_seen_${token}`)); } catch { /* stockage indisponible */ }
  }, [token]);
  const hasNewPaie = !!(latestPaiePub && latestPaiePub !== paieSeen);
  const markPaieSeen = () => {
    if (!latestPaiePub) return;
    try { localStorage.setItem(`shariti_paie_seen_${token}`, latestPaiePub); } catch { /* stockage indisponible */ }
    setPaieSeen(latestPaiePub);
  };

  const tabs = boot ? [
    ...(boot.can_pointer ? [{ key: "pointage", label: "Pointage", icon: CalendarClock }] : []),
    { key: "planning", label: "Planning", icon: CalendarDays },
    { key: "perf", label: "Ma perf", icon: TrendingUp },
    { key: "qualite", label: "Qualité", icon: Award },
    ...(boot.rebook_allowed ? [{ key: "primes", label: "Bonus spot", icon: Wallet }] : []),
    ...(showPaie ? [{ key: "paie", label: "Ma paie", icon: Receipt }] : []),
  ] : [];

  return (
    <div className="min-h-screen bg-[var(--bg-subtle)]">
      <div className={`mx-auto flex min-h-screen w-full flex-col px-4 pb-10 pt-6 ${tab === "paie" ? "max-w-md md:max-w-3xl" : "max-w-md"}`}>
        <header className="mb-5 flex items-center gap-2.5" data-testid="pointage-header">
          <img src="/shariti-logo.webp" alt="Shariti" className="h-9 w-9 object-contain" />
          <div className="leading-none">
            <div className="text-[15px] font-extrabold tracking-tight text-[var(--shariti-orange)]">SHARITI</div>
            <div className="text-[9px] font-light tracking-[0.22em] text-muted-ink">ESPACE COLLABORATEUR</div>
          </div>
        </header>

        {bootLoading && <LoadingState />}
        {!bootLoading && bootErr && <ErrorState code={bootErr.code} message={bootErr.message} />}
        {!bootLoading && !bootErr && boot && (
          <>
            <div className="mb-4" data-testid="pointage-identity">
              <div className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Collaborateur</div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xl font-extrabold text-ink" data-testid="pointage-rd-name">{boot.rd_name}</span>
                {boot.code_rd && (
                  <span className="rounded-md bg-[var(--surface-2)] px-1.5 py-0.5 font-mono text-[12px] font-bold text-[var(--shariti-orange)]"
                    data-testid="pointage-rd-code">{boot.code_rd}</span>
                )}
                {boot.is_independant && (
                  <span className="rounded bg-[rgba(93,63,211,0.12)] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#5D3FD3]">Indépendant</span>
                )}
              </div>
            </div>

            {/* Bandeau LECTURE SEULE — l'accès est restreint à la consultation (départ). */}
            {boot.is_readonly && (
              <div className="mb-3 flex items-start gap-2 rounded-2xl border border-[rgba(178,106,0,0.35)] bg-[rgba(238,114,46,0.08)] p-3" data-testid="collab-readonly-banner">
                <Eye className="mt-0.5 h-4 w-4 shrink-0 text-[#B26A00]" />
                <div className="min-w-0">
                  <div className="text-[13px] font-extrabold text-ink">Accès en lecture seule</div>
                  <p className="mt-0.5 text-[12px] leading-snug text-muted-ink">
                    Vous pouvez consulter vos informations, mais toute modification (pointage, rebooking) est désactivée. Contactez votre administrateur.
                  </p>
                </div>
              </div>
            )}

            {rebookAlerts.length > 0 && (
              <div className="mb-3 rounded-2xl border border-[rgba(238,114,46,0.4)] bg-[rgba(238,114,46,0.08)] p-3" data-testid="rebook-alerts-banner">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
                  <span className="text-[13px] font-extrabold text-ink">
                    {rebookAlerts.length === 1
                      ? "Un spot que vous suiviez est de nouveau libre"
                      : `${rebookAlerts.length} spots que vous suiviez sont de nouveau libres`}
                  </span>
                </div>
                <ul className="mt-2 space-y-1.5">
                  {rebookAlerts.map((a) => (
                    <li key={a.spot_id} className="flex items-center gap-2 rounded-xl bg-white/70 px-2.5 py-1.5" data-testid={`rebook-alert-${a.spot_id}`}>
                      <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-ink">{a.spot_name || "Spot"}</span>
                      <button type="button" onClick={() => setTab("planning")} data-testid={`rebook-alert-open-${a.spot_id}`}
                        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--shariti-orange)] px-2 py-0.5 text-[11px] font-bold text-white transition-opacity hover:opacity-90">
                        <CalendarPlus className="h-3 w-3" /> Rebooker
                      </button>
                      <button type="button" onClick={() => dismissAlert(a.spot_id)} data-testid={`rebook-alert-dismiss-${a.spot_id}`}
                        title="Ignorer" className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-muted-ink transition-colors hover:bg-[var(--surface-2)]">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Rappel « série en jeu » — quand une série est en cours mais qu'aucun
                bulletin n'a encore été compté aujourd'hui (in-app, refermable). */}
            {showReminder && (
              <StreakReminderBanner streak={stats?.current_streak || 0} onDismiss={dismissReminder} />
            )}

            {/* Alerte ROUGE — ratio de la semaine sous l'objectif (in-app, refermable). */}
            {showRatioAlert && (
              <RatioAlertBanner ratios={stats?.ratios} onDismiss={dismissRatioAlert} />
            )}

            {/* Alerte VERTE — le ratio de la semaine repasse au vert (félicitations). */}
            {showRatioGreen && (
              <RatioGreenBanner ratios={stats?.ratios} onDismiss={dismissRatioGreen} />
            )}

            {/* Récap HEBDO — le lundi, bilan de la semaine passée. */}
            {showRecap && (
              <WeeklyRecapBanner recap={stats?.recap_semaine} onDismiss={dismissRecap} />
            )}

            {/* Bandeau gamifié TOUJOURS visible — met la performance en avant sans
                avoir à ouvrir « Ma perf ». Cliquable pour ouvrir le détail. */}
            <PerfStrip stats={stats} onOpen={() => setTab("perf")} />

            {/* Onglets de l'espace collaborateur : Pointage (salariés), Planning, Ma perf. */}
            <div className="mb-4 flex items-center gap-1 rounded-xl border border-[var(--border-soft)] bg-white p-1 shadow-[var(--shadow-sm)]" data-testid="espace-tabs">
              {tabs.map((t) => {
                const Icon = t.icon;
                const active = tab === t.key;
                const showDot = t.key === "paie" && hasNewPaie;
                return (
                  <button key={t.key} onClick={() => { setTab(t.key); if (t.key === "paie") markPaieSeen(); }} data-testid={`espace-tab-${t.key}`}
                    className={`relative flex flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-1.5 py-1.5 text-[11px] font-bold leading-tight text-center transition-colors ${active ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                    {showDot && (
                      <span className="absolute right-1.5 top-1 h-2 w-2 rounded-full bg-[var(--shariti-orange)] ring-2 ring-white" data-testid="ma-paie-new-dot" aria-label="Nouveau relevé de paie" />
                    )}
                    <Icon className="h-4 w-4 shrink-0" /> <span>{t.label}</span>
                  </button>
                );
              })}
            </div>

            {tab === "pointage" && boot.can_pointer && <PointageTab token={token} readOnly={!!boot.is_readonly} />}
            {tab === "planning" && <CollabPlanning token={token} />}
            {tab === "perf" && <CollabDashboard token={token} preloaded={stats} />}
            {tab === "qualite" && <CollabQualite token={token} />}
            {tab === "primes" && <PrimesTab token={token} />}
            {tab === "paie" && showPaie && <MaPaie data={paie} />}
          </>
        )}
      </div>

      {/* Célébration de PASSAGE DE NIVEAU (plein écran, tous onglets confondus) */}
      {levelUp && <LevelUpCelebration level={levelUp} onClose={closeLevelUp} />}

      {/* Annonces de la Direction — pop-up bloquant (accusé de lecture obligatoire) */}
      <AnnouncementModal token={token} scope="public" />
    </div>
  );
}

// Onglet POINTAGE (salariés / PAP) — logique et rendu du pointage du jour + heures du mois.
function PointageTab({ token, readOnly = false }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [acting, setActing] = useState(false);
  const [geoBlock, setGeoBlock] = useState(null);
  const [excSubmitting, setExcSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await publicApi.get(`/pointage/public/${token}`);
      setData(r.data);
      setError(null);
    } catch (e) {
      const code = e?.response?.status;
      setError({ code, message: e?.response?.data?.detail || "Lien indisponible." });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const pointer = async () => {
    if (acting) return;
    setActing(true);
    setGeoBlock(null);
    const pos = await getPosition();
    try {
      const body = pos ? { lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy } : { gps_denied: true };
      const r = await postPointerWithRetry(token, body);
      if (r.data?.blocked) {
        setData(r.data);
        setGeoBlock({
          reason: r.data.reason, distance_m: r.data.distance_m, precision_m: r.data.precision_m,
          rayon_m: r.data.rayon_m, spot_address: r.data.spot_address, spot_name: r.data.spot_name,
          kind: r.data.kind, message: r.data.message,
          lat: pos?.lat, lng: pos?.lng, accuracy: pos?.accuracy,
        });
        return;
      }
      setData(r.data);
      if (r.data.already) toast.info("Pointage déjà enregistré.");
      else toast.success(`${r.data.kind === "arrivee" ? "Arrivée" : "Départ"} enregistré(e) à ${r.data.heure}.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Connexion instable — votre pointage n'a pas été pris en compte. Réessayez.");
    } finally {
      setActing(false);
    }
  };

  const submitException = async () => {
    if (!geoBlock || excSubmitting) return;
    setExcSubmitting(true);
    const pos = await getPosition();
    try {
      const body = {
        kind: geoBlock.kind, lat: pos?.lat ?? geoBlock.lat ?? null,
        lng: pos?.lng ?? geoBlock.lng ?? null, accuracy: pos?.accuracy ?? geoBlock.accuracy ?? null,
        motif: geoBlock.reason,
      };
      const r = await publicApi.post(`/pointage/public/${token}/pointer-exceptionnel`, body, { timeout: 12000 });
      setData(r.data);
      setGeoBlock(null);
      toast.success(`Demande enregistrée à ${r.data.heure} — en attente de validation.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible. Réessayez.");
    } finally {
      setExcSubmitting(false);
    }
  };

  // Confirmation des heures RETIRÉE côté collaborateur (sur demande) : plus de bouton
  // « Je confirme mes heures » ni d'appel à /confirm-mois depuis l'espace collaborateur.

  return (
    <div data-testid="pointage-tab">
      {loading && <LoadingState />}
      {!loading && error && <ErrorState code={error.code} message={error.message} />}

      {!loading && !error && data && (
        data.pap_mode ? (
          <PapHoursView data={data} />
        ) : (
          <>
            {!data.mission ? (
              <NoMission />
            ) : geoBlock ? (
              <GeoBlockCard block={geoBlock} acting={acting} excSubmitting={excSubmitting}
                onRefresh={pointer} onException={submitException} onCancel={() => setGeoBlock(null)} />
            ) : (
              <MissionCard mission={data.mission} pointage={data.pointage} acting={acting} onPointer={pointer}
                token={token} rdName={data.rd_name} onRebooked={load} rebookAllowed={data.rebook_allowed}
                readOnly={readOnly} />
            )}

            <MonthCard
              mois={data.mois}
              total={data.total_heures_mois}
              bulletinsMois={data.bulletins_mois}
              jours={data.jours_mois}
              moisPrecedents={data.mois_precedents}
            />

            <p className="mt-6 text-center text-[11px] leading-relaxed text-muted-ink" data-testid="pointage-legal">
              Ajoutez cette page à votre écran d'accueil : votre lien est personnel et permanent.
              L'horodatage est enregistré côté serveur. Votre position n'est utilisée qu'au moment du pointage.
            </p>
          </>
        )
      )}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-4" data-testid="pointage-loading">
      <Skeleton className="h-6 w-40" />
      <Skeleton className="h-40 w-full rounded-2xl" />
      <Skeleton className="h-24 w-full rounded-2xl" />
    </div>
  );
}

function ErrorState({ code, message }) {
  const revoked = code === 410;
  return (
    <div className="mt-10 rounded-2xl border border-[var(--border-soft)] bg-white p-6 text-center shadow-[var(--shadow-sm)]"
      data-testid="pointage-error">
      <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-[rgba(214,69,69,0.10)]">
        <AlertCircle className="h-6 w-6 text-[#9B2226]" />
      </div>
      <h2 className="text-lg font-extrabold text-ink">{revoked ? "Lien désactivé" : "Lien indisponible"}</h2>
      <p className="mt-1 text-sm text-muted-ink">{message || "Contactez votre responsable."}</p>
    </div>
  );
}

function NoMission() {
  return (
    <div className="rounded-2xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-8 text-center"
      data-testid="pointage-no-mission">
      <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-white shadow-[var(--shadow-sm)]">
        <CalendarX2 className="h-6 w-6 text-[var(--shariti-orange)]" />
      </div>
      <h2 className="text-lg font-extrabold text-ink">Aucune mission aujourd'hui</h2>
      <p className="mt-1 text-sm text-muted-ink">Revenez le jour de votre prochaine affectation.</p>
    </div>
  );
}

/* --------------------------------------- Écrêtage : transparence pointé → retenu */
function EcretageNotes({ notes, testid }) {
  if (!notes || notes.length === 0) return null;
  return (
    <div className="mt-2 space-y-1.5" data-testid={testid}>
      {notes.map((n, i) => (
        <div key={i} className="flex items-start gap-1.5 rounded-lg border border-[rgba(47,111,237,0.22)] bg-[rgba(47,111,237,0.06)] px-2.5 py-1.5 text-[11px] leading-snug text-[#1E4FB0]"
          data-testid={`${testid}-${n.type}`}>
          <Scissors className="mt-0.5 h-3 w-3 shrink-0" />
          <span><span className="font-bold">{n.label}</span><span className="mt-0.5 block text-[#1E4FB0]/85">{n.explain}</span></span>
        </div>
      ))}
    </div>
  );
}

function GeoBlockCard({ block, acting, excSubmitting, onRefresh, onException, onCancel }) {
  const kindLabel = block.kind === "depart" ? "départ" : "arrivée";
  const title = BLOCK_TITLE[block.reason] || "Pointage impossible pour l'instant";
  const noGps = block.reason === "spot_sans_gps";
  const gpsOff = block.reason === "gps_indisponible" || block.reason === "gps_refuse";
  const busy = acting || excSubmitting;
  return (
    <div className="rounded-2xl border border-[rgba(238,114,46,0.35)] bg-white p-5 shadow-[var(--shadow-md)]" data-testid="pointage-geoblock">
      <div className="flex items-start gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--surface-2)]">
          <Navigation className="h-5 w-5 text-[var(--shariti-orange)]" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Pointage {kindLabel}</div>
          <h2 className="mt-0.5 text-lg font-extrabold leading-tight text-ink" data-testid="geoblock-title">{title}</h2>
        </div>
      </div>
      {block.reason === "hors_zone" && (
        <div className="mt-4 grid grid-cols-2 gap-3" data-testid="geoblock-distance-info">
          <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3 text-center">
            <div className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Votre distance</div>
            <div className="mt-1 text-2xl font-extrabold tabular-nums text-[var(--shariti-orange)]" data-testid="geoblock-distance">
              {block.distance_m != null ? `${block.distance_m} m` : "—"}
            </div>
          </div>
          <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3 text-center">
            <div className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Rayon autorisé</div>
            <div className="mt-1 text-2xl font-extrabold tabular-nums text-ink" data-testid="geoblock-rayon">
              {block.rayon_m != null ? `${block.rayon_m} m` : "—"}
            </div>
          </div>
        </div>
      )}
      <p className="mt-4 text-sm leading-relaxed text-muted-ink" data-testid="geoblock-explain">
        {block.reason === "hors_zone" && (<>Rapprochez-vous du point de pointage, puis actualisez votre position. Le pointage se débloque automatiquement une fois dans la zone.</>)}
        {gpsOff && (<>Activez la localisation dans les réglages de votre téléphone (et autorisez ce site), puis actualisez. La position n'est lue qu'au moment du pointage.</>)}
        {noGps && (<>{block.message || "Ce spot n'a pas encore de coordonnées GPS enregistrées."} Vous pouvez signaler votre présence : votre responsable validera l'horaire.</>)}
        {block.precision_m != null && block.reason === "hors_zone" && (<span className="mt-1 block text-[11px] text-muted-ink">Précision GPS estimée : ± {block.precision_m} m.</span>)}
      </p>
      {block.spot_address && (
        <div className="mt-3 flex items-start gap-1.5 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-sm text-ink" data-testid="geoblock-address">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
          <span>{block.spot_name && <span className="block font-semibold">{block.spot_name}</span>}<span className="text-muted-ink">{block.spot_address}</span></span>
        </div>
      )}
      <div className="mt-5 space-y-2">
        {!noGps && (
          <Button onClick={onRefresh} disabled={busy} data-testid="geoblock-refresh-btn"
            className="h-13 w-full rounded-xl bg-[var(--shariti-orange)] py-3 text-base font-extrabold text-white shadow-[var(--shadow-md)] transition-colors hover:bg-[#d9631f] disabled:opacity-70">
            {acting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Nouvelle tentative…</>) : (<><Navigation className="mr-2 h-5 w-5" /> Actualiser ma position</>)}
          </Button>
        )}
        <Button variant="outline" onClick={onException} disabled={busy} data-testid="geoblock-exception-btn"
          className="h-auto w-full whitespace-normal rounded-xl border-[var(--border-soft)] py-3 text-sm font-semibold leading-snug text-ink hover:bg-[var(--surface-2)] disabled:opacity-70">
          {excSubmitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Envoi de la demande…</>) : (<><HelpCircle className="mr-2 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" /> Je suis sur place mais mon GPS ne me localise pas</>)}
        </Button>
        {!noGps && (
          <button type="button" onClick={onCancel} disabled={busy} data-testid="geoblock-cancel-btn"
            className="w-full py-1 text-center text-[12px] font-semibold text-muted-ink transition-colors hover:text-ink disabled:opacity-50">Retour</button>
        )}
      </div>
      <p className="mt-3 text-center text-[11px] leading-relaxed text-muted-ink">
        La demande enregistre l'heure immédiatement. Elle sera comptée dans vos heures une fois validée par votre responsable.
      </p>
    </div>
  );
}

function PapHoursView({ data }) {
  // Vue dédiée PORTE-À-PORTE : consultation pure des heures (aucune notion de pointage).
  const [openDays, setOpenDays] = useState(false);
  const cumul = data.cumul_total || 0;
  const moisTous = data.mois_tous || [];
  const jours = (data.jours_mois || []).filter((j) => j.heures != null || j.en_cours);

  return (
    <div className="space-y-4" data-testid="pap-hours-view">
      {/* Cumul total */}
      <div className="rounded-2xl border border-[rgba(238,114,46,0.25)] bg-gradient-to-br from-[#fff3ea] to-white p-6 shadow-[var(--shadow-md)]" data-testid="pap-cumul-card">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
          <TrendingUp className="h-4 w-4 text-[var(--shariti-orange)]" /> Cumul total de vos heures
        </div>
        <div className="mt-2 flex items-baseline gap-1.5">
          <span className="text-5xl font-extrabold tabular-nums text-[var(--shariti-orange)]" data-testid="pap-cumul-total">{fmtH(cumul)}</span>
          <span className="text-xl font-bold text-muted-ink">h</span>
        </div>
        <p className="mt-2 text-[12px] leading-relaxed text-muted-ink">
          Total de toutes vos heures enregistrées. Vérifiez qu'il correspond à vos fiches de paie.
        </p>
      </div>

      {/* Mois en cours + détail jour par jour */}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]" data-testid="pap-mois-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-bold capitalize text-ink">
            <CalendarDays className="h-4 w-4 text-[var(--shariti-orange)]" /> {frMonth(data.mois)}
          </div>
          <span className="text-lg font-extrabold tabular-nums text-ink" data-testid="pap-mois-total">{fmtH(data.total_heures_mois)} h</span>
        </div>
        {jours.length > 0 ? (
          <>
            <button type="button" onClick={() => setOpenDays((v) => !v)}
              className="mt-3 flex w-full items-center justify-between rounded-lg bg-[var(--surface-2)] px-3 py-2 text-[13px] font-semibold text-ink transition-colors hover:bg-[var(--border-soft)]"
              data-testid="pap-toggle-days">
              <span>Détail jour par jour ({jours.length})</span>
              {openDays ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {openDays && (
              <div className="mt-2 divide-y divide-[var(--border-soft)] overflow-hidden rounded-lg border border-[var(--border-soft)]" data-testid="pap-days-list">
                {jours.map((j) => (
                  <div key={j.date} className="flex items-center justify-between px-3 py-2 text-[13px]" data-testid={`pap-day-${j.date}`}>
                    <span className="font-semibold tabular-nums text-ink">{frDay(j.date)}</span>
                    {j.en_cours ? (
                      <span className="text-[12px] font-bold text-[var(--shariti-orange)]">en cours</span>
                    ) : (
                      <span className="font-extrabold tabular-nums text-[var(--shariti-orange)]">{fmtH(j.heures)} h</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="mt-3 text-[13px] text-muted-ink" data-testid="pap-mois-empty">Aucune heure enregistrée ce mois-ci.</p>
        )}
      </div>

      {/* Historique mois par mois */}
      {moisTous.length > 1 && (
        <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]" data-testid="pap-historique-card">
          <div className="mb-1 flex items-center gap-2 text-sm font-bold text-ink">
            <Clock className="h-4 w-4 text-[var(--shariti-orange)]" /> Historique mois par mois
          </div>
          <div className="divide-y divide-[var(--border-soft)]">
            {moisTous.map((m) => (
              <div key={m.mois} className="flex items-center justify-between py-2.5 text-[14px]" data-testid={`pap-month-${m.mois}`}>
                <span className="font-semibold capitalize text-ink">{frMonth(m.mois)}</span>
                <span className="font-extrabold tabular-nums text-ink">{fmtH(m.total)} h</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// Message WhatsApp pré-rempli (contexte : problème d'accès sur le spot).
function waUrl(e164, spotName, rdName) {
  const msg =
    `Bonjour, je suis ${rdName || "un recruteur"} (terrain Shariti). ` +
    `Je rencontre un problème d'accès au spot ${spotName || "prévu"}. ` +
    `Peux-tu m'aider s'il te plaît ?`;
  return `https://wa.me/${e164}?text=${encodeURIComponent(msg)}`;
}

// Bloc DISCRET « Un souci d'accès ? » : par défaut un simple petit lien qui ne mange
// pas le planning ; au tap, il déplie « Réservé par X » + 2 petits boutons Appeler /
// WhatsApp (ou un repli si aucun numéro). Sert au RD si l'accueil lui refuse l'accès.
function BookerContact({ booker, spotName, rdName }) {
  const [open, setOpen] = useState(false);
  if (!booker) return null;
  const has = booker.has_phone;
  return (
    <div className="mt-2" data-testid="booker-contact">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="booker-toggle"
        className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold text-muted-ink transition-colors hover:text-[var(--shariti-orange)]"
        aria-expanded={open}>
        <LifeBuoy className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
        Un souci d'accès&nbsp;?
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1.5 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5">
          <div className="flex items-center gap-1.5 text-[11px] text-ink">
            <UserRound className="h-3.5 w-3.5 shrink-0 text-muted-ink" />
            <span>Réservé par <span className="font-bold">{booker.name}</span></span>
          </div>
          {has ? (
            <div className="mt-2 flex gap-2">
              <a href={`tel:+${booker.phone_e164}`} data-testid="booker-call"
                className="inline-flex h-8 flex-1 items-center justify-center gap-1 rounded-lg bg-[var(--shariti-orange)] text-[12px] font-bold text-white transition-colors hover:bg-[#d9631f]">
                <Phone className="h-3.5 w-3.5" /> Appeler
              </a>
              <a href={waUrl(booker.phone_e164, spotName, rdName)} target="_blank" rel="noopener noreferrer" data-testid="booker-whatsapp"
                className="inline-flex h-8 flex-1 items-center justify-center gap-1 rounded-lg bg-[#1FA855] text-[12px] font-bold text-white transition-colors hover:bg-[#178a45]">
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
              </a>
            </div>
          ) : (
            <div className="mt-1.5 text-[11px] leading-snug text-muted-ink">
              Numéro non renseigné. Demandez à votre responsable de l'ajouter dans son profil.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Rebooking TERRAIN : le RD, sur place, a l'accord du directeur → il pose la/les date(s),
// PRÉVISUALISE l'email « Je confirme » (identique aux CDP), puis l'envoie. Le rebooking
// lui est crédité (prime 50 €). Modale en 2 étapes : calendrier → aperçu → envoi.
function RebookDialog({ token, bookingId, spotName, onDone, lock }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("dates");      // "dates" | "preview"
  const [dates, setDates] = useState([]);
  const [preview, setPreview] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  // Alerte libération (Phase 26) : « Prévenez-moi » quand ce spot verrouillé se libère
  const [watched, setWatched] = useState(!!lock?.watched);
  const [watchBusy, setWatchBusy] = useState(false);
  useEffect(() => { setWatched(!!lock?.watched); }, [lock?.watched]);
  const toggleWatch = async () => {
    setWatchBusy(true);
    const next = !watched;
    try {
      await publicApi.post(`/pointage/public/${token}/rebook/watch`, { booking_id: bookingId, on: next });
      setWatched(next);
      toast.success(next
        ? "Alerte activée — vous serez prévenu ici dès que ce spot se libère."
        : "Alerte désactivée.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible.");
    } finally { setWatchBusy(false); }
  };

  const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
  const reset = () => { setStep("dates"); setDates([]); setPreview(null); setRecipientEmail(""); setCustomNote(""); };
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim());

  const goPreview = async () => {
    if (!dates.length) { toast.error("Choisissez au moins une date."); return; }
    setLoading(true);
    try {
      const iso = dates.map(toISO).sort();
      const r = await publicApi.post(`/pointage/public/${token}/rebook/preview`,
        { booking_id: bookingId, dates: iso, recipient_email: recipientEmail.trim() || undefined, custom_note: customNote.trim() || undefined });
      setPreview(r.data);
      setRecipientEmail((r.data?.recipient || r.data?.recipient_stored || "").trim());
      setStep("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aperçu impossible. Réessayez.");
    } finally { setLoading(false); }
  };

  const submit = async () => {
    if (!emailValid) { toast.error("Renseignez une adresse email valide pour le directeur du magasin."); return; }
    setSending(true);
    try {
      const iso = dates.map(toISO).sort();
      const r = await publicApi.post(`/pointage/public/${token}/rebook`,
        { booking_id: bookingId, dates: iso, send_email: true, recipient_email: recipientEmail.trim(), custom_note: customNote.trim() || undefined });
      const n = r.data?.nb_dates ?? r.data?.count ?? 0;
      const isPending = r.data?.pending;
      const prime = r.data?.prime_total || 0;
      const es = r.data?.email?.status;
      const to = r.data?.recipient;
      if (n > 0) {
        const mailTxt = es === "sent" ? ` Email de confirmation envoyé à ${to}.`
          : (es && String(es).startsWith("skipped")) ? " (aucune adresse — email non envoyé)"
          : es === "failed" ? " (l'email n'a pas pu partir — vérifiez l'adresse)" : "";
        const primeTxt = isPending
          ? " En attente de validation admin (hors rotation) — prime créditée une fois validée."
          : ` +${prime} € pour vous (1 emplacement).`;
        toast.success(`Spot rebooké — ${n} date${n > 1 ? "s" : ""} posée${n > 1 ? "s" : ""}.${primeTxt}${mailTxt}`);
        setOpen(false); reset();
        onDone && onDone();
      } else {
        const why = (r.data?.skipped || [])[0]?.reason;
        toast.info(why ? `Aucune date posée — ${why}.` : "Aucune nouvelle date posée.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebooking impossible. Réessayez.");
    } finally { setSending(false); }
  };

  return (
    <>
      {/* Verrou anti-télescopage : un collègue a déjà rebooké ce spot (visite à venir) →
          bouton désactivé et discret pour éviter le double booking. */}
      {lock?.locked && !lock?.by_me ? (
        <span className="mt-2 inline-flex flex-wrap items-center gap-1.5">
          <span
            data-testid="rebook-locked"
            title={`Déjà rebooké par ${lock.by || "un collègue"}${lock.date ? ` — visite prévue le ${frDay(lock.date)}` : ""}. Impossible de le rebooker en même temps.`}
            className="inline-flex cursor-not-allowed items-center gap-1 rounded-full border border-[var(--border-soft)] bg-[var(--surface-2)] px-2 py-0.5 text-[11px] font-semibold text-muted-ink"
          >
            <Lock className="h-3.5 w-3.5" /> Réservé par {lock.by || "un collègue"}
          </span>
          <button type="button" onClick={toggleWatch} disabled={watchBusy} data-testid="rebook-watch-toggle"
            title={watched ? "Alerte active — cliquez pour ne plus être prévenu" : "Être prévenu dès que ce spot se libère"}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold transition-colors disabled:opacity-50 ${watched
              ? "border-[rgba(238,114,46,0.5)] bg-[rgba(238,114,46,0.1)] text-[var(--shariti-orange)]"
              : "border-[var(--border-soft)] text-muted-ink hover:bg-[var(--surface-2)]"}`}>
            {watchBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />}
            {watched ? "Alerte activée" : "Prévenez-moi"}
          </button>
        </span>
      ) : (
      <>
      {lock?.by_me && (
        <span
          data-testid="rebook-mine"
          className="mt-2 mr-1.5 inline-flex items-center gap-1 rounded-full border border-[rgba(238,114,46,0.4)] bg-[rgba(238,114,46,0.08)] px-2 py-0.5 text-[11px] font-semibold text-[var(--shariti-orange)]"
        >
          <CheckCircle className="h-3.5 w-3.5" /> Rebooké par vous
        </span>
      )}
      <button type="button" onClick={() => { reset(); setOpen(true); }} data-testid="rebook-open"
        className="mt-2 inline-flex items-center gap-1 rounded-full border border-[rgba(238,114,46,0.4)] px-2 py-0.5 text-[11px] font-semibold text-[var(--shariti-orange)] transition-colors hover:bg-[rgba(238,114,46,0.08)]">
        <CalendarPlus className="h-3.5 w-3.5" /> {lock?.by_me ? "Ajouter une date" : "Rebooker ce spot"}
      </button>
      </>
      )}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-h-[88vh] max-w-[380px] overflow-y-auto p-4" data-testid="rebook-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CalendarPlus className="h-5 w-5 text-[var(--shariti-orange)]" />
              {step === "dates" ? "Rebooker ce spot" : "Aperçu de l'email"}
            </DialogTitle>
            <DialogDescription className="text-[12px] leading-snug">
              {step === "dates" ? (
                <><span className="font-bold text-ink">{spotName}</span> — le directeur est d'accord&nbsp;? Choisissez la/les date(s). Vous verrez l'email avant l'envoi ; ce rebooking sera <b>crédité à votre nom</b>.</>
              ) : (
                <>Voici l'email qui sera envoyé au magasin. Vérifiez, puis confirmez l'envoi.</>
              )}
            </DialogDescription>
          </DialogHeader>

          {step === "dates" ? (
            <>
              <div className="flex justify-center">
                <Calendar mode="multiple" selected={dates} onSelect={(d) => setDates(d || [])}
                  disabled={{ before: midnight }} weekStartsOn={1} data-testid="rebook-calendar"
                  classNames={{
                    day_selected: "bg-[var(--shariti-orange)] text-white hover:bg-[#d9631f] focus:bg-[#d9631f]",
                    day_today: "ring-1 ring-[var(--shariti-orange)]",
                  }} />
              </div>
              {dates.length > 0 && (
                <div className="rounded-lg bg-[var(--surface-2)] px-3 py-2 text-center text-[12px] font-bold text-ink" data-testid="rebook-count">
                  {dates.length} date{dates.length > 1 ? "s" : ""} sélectionnée{dates.length > 1 ? "s" : ""}
                </div>
              )}
              <div>
                <label className="mb-1 block text-[11px] font-semibold text-ink">Message personnalisé au directeur <span className="font-normal text-muted-ink">(facultatif)</span></label>
                <Textarea value={customNote} onChange={(e) => setCustomNote(e.target.value)} rows={2}
                  maxLength={400} data-testid="rebook-note"
                  placeholder="Ex. Merci encore pour votre accueil, à très vite !"
                  className="resize-none text-[13px]" />
                <div className="mt-0.5 text-right text-[10px] text-muted-ink">{customNote.length}/400</div>
              </div>
              <DialogFooter className="flex-row gap-2 sm:justify-between">
                <Button variant="outline" className="flex-1" onClick={() => setOpen(false)} data-testid="rebook-cancel">Annuler</Button>
                <Button className="flex-1 bg-[var(--shariti-orange)] text-white hover:bg-[#d9631f]"
                  onClick={goPreview} disabled={loading || !dates.length} data-testid="rebook-preview-btn">
                  {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
                  Prévisualiser
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-2.5">
                {/* Destinataires EN CLAIR — le RD voit exactement qui recevra l'email et
                    peut corriger l'adresse du directeur avant l'envoi. */}
                <div className="rounded-xl border border-[var(--border-soft)] bg-white p-3" data-testid="rebook-recipients">
                  <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-[var(--shariti-orange)]">
                    <Send className="h-3.5 w-3.5" /> Qui va recevoir cet email ?
                  </div>
                  <label className="mt-2 block text-[11px] font-semibold text-ink">Directeur du magasin (destinataire)</label>
                  <input type="email" inputMode="email" value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="ex. directeur@magasin.fr" data-testid="rebook-recipient-input"
                    className={`mt-1 w-full rounded-lg border px-2.5 py-2 text-[13px] text-ink outline-none transition-colors focus:ring-2 focus:ring-[rgba(238,114,46,0.35)] ${emailValid ? "border-[var(--border-soft)]" : "border-[#E4A33A] bg-[rgba(233,184,0,0.06)]"}`} />
                  {!recipientEmail.trim() ? (
                    <div className="mt-1 flex items-start gap-1 text-[11px] font-semibold text-[#B4531B]" data-testid="rebook-no-recipient">
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      Aucune adresse enregistrée pour ce magasin — saisissez l'email du directeur, sinon il ne recevra pas la confirmation.
                    </div>
                  ) : !emailValid ? (
                    <div className="mt-1 text-[11px] font-semibold text-[#B4531B]">Adresse email invalide.</div>
                  ) : (
                    <div className="mt-1 text-[11px] text-muted-ink">Cette adresse sera aussi enregistrée sur la fiche magasin.</div>
                  )}
                  {preview?.recipient_source === "carnet" && (
                    <div className="mt-1 flex items-start gap-1 text-[11px] font-semibold text-[#0F5A2F]" data-testid="rebook-from-carnet">
                      <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      Adresse mémorisée pour ce magasin (pré-remplie automatiquement) — modifiable.
                    </div>
                  )}
                  <div className="mt-2 space-y-0.5 border-t border-dashed border-[var(--border-soft)] pt-2 text-[11px] text-muted-ink">
                    {preview?.cc?.length > 0 && (
                      <div><span className="font-semibold text-ink">En copie :</span> {preview.cc.join(", ")}</div>
                    )}
                    <div><span className="font-semibold text-ink">Expéditeur :</span> {preview?.from_name} &lt;{preview?.from_email}&gt;</div>
                    <div className="capitalize"><span className="font-semibold text-ink">Dates :</span> {preview?.dates_label}</div>
                    {preview?.already_planned?.length > 0 && (
                      <div className="text-[#B4531B]">{preview.already_planned.length} date(s) déjà planifiée(s) seront ignorée(s).</div>
                    )}
                  </div>
                </div>
                <div className="text-[11px] font-semibold text-muted-ink">Aperçu de l'email envoyé :</div>
                <iframe title="Aperçu de l'email" data-testid="rebook-preview-iframe" sandbox=""
                  srcDoc={preview?.html || ""} className="h-[280px] w-full rounded-lg border border-[var(--border-soft)] bg-white" />
              </div>
              <DialogFooter className="flex-row gap-2 sm:justify-between">
                <Button variant="outline" className="flex-1" onClick={() => setStep("dates")} data-testid="rebook-back">Retour</Button>
                <Button className="flex-1 bg-[var(--shariti-orange)] text-white hover:bg-[#d9631f]"
                  onClick={submit} disabled={sending || !emailValid} data-testid="rebook-submit">
                  {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
                  Confirmer & envoyer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function primeDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(String(iso).length <= 10 ? iso + "T00:00:00" : iso);
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

// Onglet « Mes primes » : chaque spot rebooké validé = 50 €. Total + détail, mise à jour
// automatique (les rebookings annulés par l'admin apparaissent barrés, sans prime).
function PrimesTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let on = true;
    publicApi.get(`/pointage/public/${token}/primes`)
      .then((r) => { if (on) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [token]);

  if (loading) return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[var(--shariti-orange)]" /></div>;
  const items = data?.items || [];
  const per = data?.amount_per || 50;
  const acquired = data?.acquired_count ?? data?.valid_count ?? 0;
  const upcomingCount = data?.upcoming_count || 0;
  const pending = data?.pending_count || 0;
  const upcomingAmt = data?.upcoming_amount || 0;
  return (
    <div className="space-y-4" data-testid="primes-tab">
      <div className="rounded-2xl bg-gradient-to-br from-[var(--shariti-orange)] to-[var(--shariti-orange-2)] p-4 text-white shadow-[var(--shadow-sm)]">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide opacity-90"><Wallet className="h-4 w-4" /> Mes bonus spot</div>
        <div className="mt-1 flex items-end gap-2">
          <span className="text-4xl font-extrabold" data-testid="primes-total-amount">{data?.total_amount || 0} €</span>
          <span className="mb-1 text-[13px] font-semibold opacity-90">{acquired} acquis{upcomingAmt ? ` · ${upcomingAmt} € à venir` : ""}</span>
        </div>
        <div className="mt-1 text-[11px] opacity-90">{per} € par emplacement · versé une fois la visite effectuée{pending ? ` · ${pending} en attente de validation` : ""}{data?.cancelled_count ? ` · ${data.cancelled_count} annulé(s)` : ""}</div>
      </div>

      {items.length === 0 ? (
        <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-12 text-center" data-testid="primes-empty">
          <Wallet className="h-7 w-7 text-muted-ink" />
          <p className="mt-2 text-sm font-bold">Aucun bonus pour l'instant</p>
          <p className="mt-1 max-w-xs text-xs text-muted-ink">Chaque emplacement que vous rebookez sur le terrain vous rapporte {per} € (une fois par magasin, versé après votre 1re visite).</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => {
            const dl = (it.dates || []).map(primeDate);
            const up = !it.cancelled && !it.pending && !it.due;   // validé mais visite pas encore passée
            const borderCls = it.cancelled ? "border-[var(--border-soft)] opacity-70" : it.pending ? "border-[rgba(233,184,0,0.55)]" : up ? "border-[rgba(37,99,235,0.35)]" : "border-[var(--border-soft)]";
            const iconWrap = it.cancelled ? "bg-[var(--surface-2)]" : it.pending ? "bg-[rgba(233,184,0,0.18)]" : up ? "bg-[rgba(37,99,235,0.12)]" : "bg-[rgba(31,157,85,0.12)]";
            const euroCls = it.cancelled ? "text-muted-ink line-through" : it.pending ? "text-[#8A6D00]" : up ? "text-[#1D4ED8]" : "text-[#0F5A2F]";
            const euroTxt = it.cancelled ? "annulé" : it.pending ? "à valider" : up ? `${per} €` : `+${per} €`;
            return (
              <div key={it.spot_id || i} className={`flex items-start gap-3 rounded-2xl border bg-white p-3 ${borderCls}`} data-testid={`primes-item-${i}`}>
                <span className={`mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl ${iconWrap}`}>
                  {it.cancelled ? <XCircle className="h-4 w-4 text-muted-ink" /> : it.pending ? <Hourglass className="h-4 w-4 text-[#8A6D00]" /> : up ? <CalendarDays className="h-4 w-4 text-[#1D4ED8]" /> : <CheckCircle className="h-4 w-4 text-[#0F5A2F]" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-bold leading-tight text-ink break-words">{it.spot_name}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-ink">
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" />{[it.ville, it.dept ? `(${it.dept})` : null].filter(Boolean).join(" ") || "—"}</span>
                    <span className="inline-flex items-center gap-1 capitalize"><CalendarDays className="h-3 w-3 shrink-0" />{dl.length <= 1 ? (dl[0] || "—") : `${dl.length} dates`}</span>
                  </div>
                  {dl.length > 1 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {dl.map((d, k) => <span key={k} className="rounded-md bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-semibold capitalize text-muted-ink">{d}</span>)}
                    </div>
                  )}
                  {it.pending && <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-[#8A6D00]"><Hourglass className="h-3 w-3" />En attente de validation admin (hors rotation)</div>}
                  {up && <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-[#1D4ED8]"><CalendarDays className="h-3 w-3" />Créditée après votre 1re visite</div>}
                </div>
                <span className={`mt-0.5 shrink-0 rounded-full px-2.5 py-1 text-[13px] font-extrabold ${euroCls}`}>
                  {euroTxt}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MissionCard({ mission, pointage, acting, onPointer, token, rdName, onRebooked, rebookAllowed, readOnly = false }) {
  const next = mission.next_action;
  const [confirmDepart, setConfirmDepart] = useState(false);
  const arr = pointage?.arrivee_pointee;
  const dep = pointage?.depart_pointe;
  const geo = pointage?.geofence_statut;
  const pending = geo === "exceptionnel_en_attente";
  const rejected = geo === "exceptionnel_rejete";
  const enCours = pointage?.en_cours;

  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-md)]" data-testid="pointage-mission-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Mission du jour</div>
          <div className="mt-0.5 break-words text-lg font-extrabold text-ink" data-testid="pointage-spot-name">{mission.spot_name}</div>
        </div>
        <Badge className="shrink-0 bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.12)]" data-testid="pointage-creneau">
          <Clock className="mr-1 h-3.5 w-3.5" /> {mission.creneau_debut}–{mission.creneau_fin}
        </Badge>
      </div>
      {mission.spot_address && (
        <div className="mt-2 flex items-start gap-1.5 text-sm text-muted-ink" data-testid="pointage-spot-address">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
          <span>{mission.spot_address}</span>
        </div>
      )}

      {/* À qui se présenter à l'accueil (responsable ayant autorisé la venue). */}
      <SpotAuthorizedBy auth={mission.authorized} testid="pointage-mission-authorized" />

      {/* Instructions du spot (consignes magasin / directeur) — capital sur le terrain. */}
      <SpotInstructions text={mission.consignes} testid="pointage-mission-instructions" />


      {mission.booker && (
        <BookerContact booker={mission.booker} spotName={mission.spot_name} rdName={rdName} />
      )}
      {/* Preuve de confirmation À MONTRER SUR PLACE (accusé signé OU e-mail envoyé). */}
      {mission.booking_id && (
        <SpotProofButton spotName={mission.spot_name}
          load={() => publicApi.get(`/pointage/public/${token}/booking/${mission.booking_id}/confirmation-proof`).then((r) => r.data)}
          testid="pointage-mission-proof" />
      )}
      {/* Rebooking terrain en 1 clic (accord obtenu sur place) — réservé aux RD autorisés */}
      {!readOnly && rebookAllowed && mission.can_rebook && mission.booking_id && (
        <RebookDialog token={token} bookingId={mission.booking_id}
          spotName={mission.spot_name} onDone={onRebooked} lock={mission.rebook_lock} />
      )}
      {pending && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-[rgba(185,137,0,0.30)] bg-[rgba(185,137,0,0.08)] px-3 py-2.5 text-sm text-[#8a6600]" data-testid="pointage-exc-pending">
          <Hourglass className="mt-0.5 h-4 w-4 shrink-0" />
          <span><span className="font-bold">Demande enregistrée — en attente de validation.</span><span className="mt-0.5 block text-[12px] text-[#8a6600]/90">Vos heures seront comptées après validation par votre responsable.</span></span>
        </div>
      )}
      {rejected && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-[rgba(214,69,69,0.30)] bg-[rgba(214,69,69,0.06)] px-3 py-2.5 text-sm text-[#9B2226]" data-testid="pointage-exc-rejected">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span><span className="font-bold">Demande non retenue.</span><span className="mt-0.5 block text-[12px]">Ces heures n'ont pas été comptées. Rapprochez-vous de votre responsable si besoin.</span></span>
        </div>
      )}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <PointStat label="Arrivée" value={arr} icon={LogIn} verified={pointage?.position_verifiee} pending={pending} testid="pointage-arrivee" />
        <PointStat label="Départ" value={dep} icon={LogOut} testid="pointage-depart" />
      </div>

      {/* Écrêtage du jour (transparence) */}
      {next === "termine" && <EcretageNotes notes={pointage?.ecretage} testid="pointage-ecretage" />}

      <div className="mt-5">
        {next === "termine" ? (
          <div className="flex items-center justify-center gap-2 rounded-xl bg-[rgba(31,157,85,0.10)] px-4 py-3 text-sm font-bold text-[#0F5A2F]" data-testid="pointage-done">
            <CheckCircle2 className="h-5 w-5" /> Journée terminée — merci !
          </div>
        ) : readOnly ? (
          <div className="flex items-center justify-center gap-2 rounded-xl bg-[var(--surface-2)] px-4 py-3 text-sm font-bold text-muted-ink" data-testid="pointage-readonly-locked">
            <Lock className="h-4 w-4" /> Pointage désactivé — accès en lecture seule
          </div>
        ) : (
          <Button onClick={() => { if (next === "depart") setConfirmDepart(true); else onPointer(); }} disabled={acting}
            data-testid={next === "arrivee" ? "pointage-btn-arrivee" : "pointage-btn-depart"}
            className="h-14 w-full rounded-xl bg-[var(--shariti-orange)] text-base font-extrabold text-white shadow-[var(--shadow-md)] transition-colors hover:bg-[#d9631f] disabled:opacity-70">
            {acting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Enregistrement…</>) : next === "arrivee" ? (<><LogIn className="mr-2 h-5 w-5" /> Pointer mon arrivée</>) : (<><LogOut className="mr-2 h-5 w-5" /> Pointer mon départ</>)}
          </Button>
        )}
        {/* Confirmation du DÉPART : évite le clic accidentel (fin de journée définitive). */}
        <AlertDialog open={confirmDepart} onOpenChange={setConfirmDepart}>
          <AlertDialogContent data-testid="pointage-confirm-depart">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2"><LogOut className="h-5 w-5 text-[var(--shariti-orange)]" /> Pointer votre départ ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cela met fin à votre journée : vous ne pourrez plus pointer aujourd'hui.
                Ne confirmez que si vous avez réellement terminé. En cas d'erreur, votre responsable pourra réinitialiser votre départ.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="pointage-confirm-depart-cancel">Pas encore</AlertDialogCancel>
              <AlertDialogAction data-testid="pointage-confirm-depart-ok"
                className="bg-[var(--shariti-orange)] hover:bg-[#d9631f]"
                onClick={() => { setConfirmDepart(false); onPointer(); }}>
                Oui, je pointe mon départ
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {/* Statut cohérent (Volet 5) : jour en cours = « en cours », jamais « — h » */}
        <div className="mt-2 text-center text-[11px] text-muted-ink" data-testid="pointage-statut">
          {next === "arrivee"
            ? "Statut : à pointer"
            : enCours
              ? "Statut : en mission — journée en cours (heures calculées au pointage du départ)"
              : (pointage?.statut
                ? <>Statut : {STATUT_LABEL[pointage.statut] || pointage.statut}{pointage.heures_retenues != null && ` · ${fmtH(pointage.heures_retenues)} h retenues`}</>
                : null)}
        </div>
      </div>
    </div>
  );
}

function PointStat({ label, value, icon: Icon, verified, pending, testid }) {
  return (
    <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-1 text-xl font-extrabold tabular-nums text-ink">{value || "—"}</div>
      {value && pending ? (
        <div className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-[#8a6600]" data-testid={`${testid}-pending`}>
          <ShieldQuestion className="h-3 w-3" /> En attente de validation
        </div>
      ) : value && verified ? (
        <div className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-[#0F5A2F]" data-testid={`${testid}-verified`}>
          <ShieldCheck className="h-3 w-3" /> Position vérifiée
        </div>
      ) : null}
    </div>
  );
}

/* --------------------------------------------------- Journée du journal personnel */
// Petite mention de CONTRÔLE DE POSITION (transparence anti-litige) : la même vérité
// que voit l'admin — pointage vérifié + distance au spot, ou pointage exceptionnel/hors zone.
function GeoMention({ j }) {
  const gs = j.geo_statut;
  const dist = j.distance_m;
  const motif = MOTIF_LABEL[j.geo_motif];
  if (gs && gs.startsWith("exceptionnel")) {
    const tone = gs === "exceptionnel_valide" ? "bg-[rgba(31,157,85,0.10)] text-[#0F5A2F]"
      : gs === "exceptionnel_rejete" ? "bg-[rgba(214,69,69,0.10)] text-[#9B2226]"
      : "bg-[rgba(185,137,0,0.14)] text-[#8a6600]";
    const label = gs === "exceptionnel_valide" ? "Pointage exceptionnel validé"
      : gs === "exceptionnel_rejete" ? "Pointage exceptionnel refusé"
      : "Pointage exceptionnel";
    return (
      <div className={`mt-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold ${tone}`}
        data-testid={`pointage-geo-${j.date}`}>
        <MapPin className="h-3 w-3 shrink-0" />
        <span>{label}{motif ? ` · ${motif}` : ""}{dist != null ? ` · ~${dist} m du spot` : ""}</span>
      </div>
    );
  }
  if ((gs === "verifiee" || j.position_verifiee) && dist != null) {
    const far = dist > 500;   // pointé nettement loin du spot → mise en évidence
    return (
      <div className={`mt-1 inline-flex items-center gap-1 text-[10px] ${far ? "font-bold text-[#9B2226]" : "text-muted-ink"}`}
        data-testid={`pointage-geo-${j.date}`}>
        <MapPin className="h-3 w-3 shrink-0" />
        <span>Pointé à ~{dist} m du spot{far ? " · loin du spot" : ""}</span>
      </div>
    );
  }
  return null;
}

function JournalDay({ j }) {
  const cls = STATUT_CLS[j.statut] || "bg-[var(--surface-2)] text-muted-ink";
  const pointe = `${j.arrivee_pointee || "—"}→${j.depart_pointe || "—"}`;
  const retenu = `${j.arrivee_retenue || "—"}→${j.depart_retenu || "—"}`;
  const diff = j.arrivee_pointee !== j.arrivee_retenue || j.depart_pointe !== j.depart_retenu;
  return (
    <div className="border-b border-[var(--border-soft)] px-3 py-2.5 last:border-0" data-testid={`pointage-journal-day-${j.date}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="font-bold tabular-nums text-ink">{frDay(j.date)}</span>
          {j.spot_name && <span className="break-words text-[11px] text-muted-ink">{j.spot_name}</span>}
        </div>
        <div className="shrink-0 text-right">
          {j.en_cours ? (
            <span className="text-[12px] font-bold text-[var(--shariti-orange)]" data-testid={`pointage-journal-encours-${j.date}`}>en cours</span>
          ) : (
            <span className={`font-extrabold tabular-nums ${j.deduit ? "text-[#2F6FED]" : "text-ink"}`}>{fmtH(j.heures)} h</span>
          )}
          {j.bulletins != null && (
            <div
              className={`mt-0.5 text-[10px] font-bold tabular-nums ${
                j.bs_zone === "vert" ? "text-[#0F5A2F]"
                  : j.bs_zone === "orange" ? "text-[#8a6600]"
                  : j.bs_zone === "rouge" ? "text-[#9B2226]"
                  : "text-[var(--shariti-orange)]"
              }`}
              data-testid={`pointage-journal-bs-${j.date}`}
              data-zone={j.bs_zone || "na"}
              title={j.bs_zone ? "Couleur selon votre productivité du jour (BS/h)" : undefined}
            >
              {j.bulletins} BS
            </div>
          )}
        </div>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-ink">
        <span>Pointé <b className="text-ink/80">{pointe}</b></span>
        {!j.en_cours && diff && <span>· Retenu <b className="text-ink/80">{retenu}</b></span>}
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${cls}`}>{STATUT_LABEL[j.statut] || j.statut}</span>
      </div>
      <EcretageNotes notes={j.ecretage} testid={`pointage-journal-ecretage-${j.date}`} />
      <GeoMention j={j} />
    </div>
  );
}

// Mois PRÉCÉDENT dépliable : en-tête (mois, BS, total h) + détail complet par jour.
function PrevMonthRow({ m }) {
  const [open, setOpen] = useState(false);
  const jours = m.jours || [];
  return (
    <div className="overflow-hidden rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)]" data-testid={`pointage-prevmonth-${m.mois}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`pointage-prevmonth-toggle-${m.mois}`}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-[12px] transition-colors hover:bg-[rgba(238,114,46,0.06)]"
      >
        <span className="flex items-center gap-2">
          {open ? <ChevronUp className="h-3.5 w-3.5 text-muted-ink" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-ink" />}
          <span className="font-semibold capitalize text-ink">{frMonth(m.mois)}</span>
        </span>
        <span className="flex items-center gap-2">
          {m.bulletins != null && (
            <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold tabular-nums text-[var(--shariti-orange)]" data-testid={`pointage-prevmonth-bs-${m.mois}`}>
              {m.bulletins} BS
            </span>
          )}
          <span className="tabular-nums font-bold text-ink" data-testid={`pointage-prevmonth-total-${m.mois}`}>{fmtH(m.total)} h</span>
        </span>
      </button>
      {open && (
        jours.length > 0 ? (
          <div className="max-h-64 overflow-y-auto border-t border-[var(--border-soft)] bg-white" data-testid={`pointage-prevmonth-days-${m.mois}`}>
            {jours.map((j) => <JournalDay key={j.date} j={j} />)}
          </div>
        ) : (
          <p className="border-t border-[var(--border-soft)] bg-white px-3 py-3 text-center text-[12px] text-muted-ink">
            Aucune journée pointée ce mois-ci.
          </p>
        )
      )}
    </div>
  );
}

function MonthCard({ mois, total, bulletinsMois, jours, moisPrecedents }) {
  const [showPrev, setShowPrev] = useState(false);
  const joursComptes = (jours || []).filter((j) => !j.en_cours && j.heures != null).length;

  return (
    <div className="mt-4 rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]" data-testid="pointage-month-card">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
        <CalendarClock className="h-3.5 w-3.5" /> Mes heures — {mois}
      </div>

      {/* Récapitulatif cumulatif du mois (vue synthétique en un coup d'œil) */}
      <div className="mt-3 grid grid-cols-3 gap-2" data-testid="pointage-month-summary">
        <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3 text-center">
          <div className="text-[10px] font-bold uppercase tracking-wide text-muted-ink">Total retenu</div>
          <div className="mt-1 text-xl font-extrabold tabular-nums text-[var(--shariti-orange)]" data-testid="pointage-month-total">{fmtH(total)} h</div>
        </div>
        <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3 text-center">
          <div className="text-[10px] font-bold uppercase tracking-wide text-muted-ink">BS du mois</div>
          <div className="mt-1 text-xl font-extrabold tabular-nums text-[var(--shariti-orange)]" data-testid="pointage-month-bs">{bulletinsMois != null ? bulletinsMois : "—"}</div>
        </div>
        <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3 text-center">
          <div className="text-[10px] font-bold uppercase tracking-wide text-muted-ink">Jours</div>
          <div className="mt-1 text-xl font-extrabold tabular-nums text-ink" data-testid="pointage-month-jours">{joursComptes}</div>
        </div>
      </div>

      {/* Journal détaillé par jour */}
      <div className="mt-4 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Détail par jour</div>
      {jours && jours.length > 0 ? (
        <div className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-[var(--border-soft)]" data-testid="pointage-month-table">
          {jours.map((j) => <JournalDay key={j.date} j={j} />)}
        </div>
      ) : (
        <p className="mt-2 rounded-xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-4 text-center text-sm text-muted-ink" data-testid="pointage-month-empty">
          Aucune heure enregistrée ce mois-ci.
        </p>
      )}

      {/* Mois précédents (lecture seule) */}
      {moisPrecedents && moisPrecedents.length > 0 && (
        <div className="mt-3">
          <button type="button" onClick={() => setShowPrev((v) => !v)} data-testid="pointage-prevmonths-toggle"
            className="flex w-full items-center justify-between rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-[12px] font-bold text-ink">
            <span>Mois précédents</span>
            {showPrev ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {showPrev && (
            <div className="mt-2 space-y-1.5" data-testid="pointage-prevmonths">
              {moisPrecedents.map((m) => <PrevMonthRow key={m.mois} m={m} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// PLANNING COLLABORATEUR — lecture seule, uniquement les lignes de SON chef d'équipe.
// ============================================================================
const COLLAB_BK_STATUS = {
  ferme: { txt: "Ferme", cls: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]" },
  confirme: { txt: "Confirmé", cls: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]" },
  option: { txt: "Option", cls: "bg-[rgba(185,137,0,0.14)] text-[#8a6600]" },
  annule: { txt: "Annulé", cls: "bg-[var(--surface-2)] text-muted-ink line-through" },
};

// Carte MAGASIN d'une journée (vue planning collaborateur « jour par jour »).
// Affiche le lieu, le créneau, le statut et — repliable — l'équipe du jour.
function PlanStoreCard({ asg, token, rdName, onRebooked, rebookAllowed }) {
  const [showTeam, setShowTeam] = useState(false);
  const st = COLLAB_BK_STATUS[asg.status] || { txt: asg.status_label || asg.status, cls: "bg-[var(--surface-2)] text-muted-ink" };
  const cancelled = asg.status === "annule";
  const team = Array.isArray(asg.equipe) ? asg.equipe : [];
  const hasTeam = team.length > 0;
  return (
    <div
      className={`rounded-xl border px-3 py-2.5 ${asg.mine ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.06)] ring-1 ring-[rgba(238,114,46,0.25)]" : "border-[var(--border-soft)] bg-white"}`}
      data-testid={`collab-plan-store-${asg.booking_id}`}
    >
      {/* Repère de contexte : renfort / affecté ici / avec l'équipe */}
      <div className="mb-1 flex flex-wrap items-center gap-1.5">
        {asg.foreign_team ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-[var(--shariti-orange)] px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white" data-testid={`collab-plan-renfort-${asg.booking_id}`}>
            <MapPin className="h-2.5 w-2.5" /> Renfort — autre équipe
          </span>
        ) : asg.mine ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-[var(--shariti-orange)] px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white" data-testid={`collab-plan-mine-${asg.booking_id}`}>
            <MapPin className="h-2.5 w-2.5" /> Vous êtes affecté ici
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(24,24,24,0.06)] px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-muted-ink" data-testid={`collab-plan-team-badge-${asg.booking_id}`}>
            <Users className="h-2.5 w-2.5" /> Avec votre équipe
          </span>
        )}
        {asg.assoc?.acronym && (
          <span className="inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-[9px] font-extrabold text-white" style={{ background: asg.assoc?.color || "#EE722E" }}>{asg.assoc.acronym}</span>
        )}
        {asg.canal_nom && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[rgba(24,24,24,0.06)] px-2 py-0.5 text-[9px] font-bold text-muted-ink"><Store className="h-2.5 w-2.5" />{asg.canal_nom}</span>
        )}
      </div>

      {/* Magasin */}
      <div className={`break-words text-[14px] font-extrabold leading-tight ${cancelled ? "text-muted-ink line-through" : "text-ink"}`} data-testid={`collab-plan-spotname-${asg.booking_id}`}>{asg.spot_name || "Spot"}</div>

      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${st.cls}`}>{st.txt}</span>
        {(asg.creneau_debut || asg.creneau_fin) && (
          <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-ink"><Clock className="h-3 w-3 text-[var(--shariti-orange)]" />{asg.creneau_debut || "—"}–{asg.creneau_fin || "—"}</span>
        )}
      </div>

      {asg.spot_address && (
        <div className="mt-1 inline-flex min-w-0 items-start gap-1 text-[11px] text-muted-ink"><MapPin className="mt-0.5 h-3 w-3 shrink-0" /><span className="break-words">{asg.spot_address}</span></div>
      )}

      {/* À qui se présenter à l'accueil (responsable ayant autorisé la venue). */}
      {!cancelled && <SpotAuthorizedBy auth={asg.authorized} testid={`collab-plan-authorized-${asg.booking_id}`} />}

      {/* Instructions du spot (consignes magasin / directeur) — capital sur le terrain. */}
      {!cancelled && <SpotInstructions text={asg.consignes} testid={`collab-plan-instructions-${asg.booking_id}`} dense />}

      {/* Joindre le booker + rebooking terrain (accès refusé / accord sur place) */}
      {!cancelled && asg.booker && (
        <BookerContact booker={asg.booker} spotName={asg.spot_name} rdName={rdName} />
      )}
      {/* Preuve de confirmation À MONTRER SUR PLACE (accusé signé OU e-mail envoyé). */}
      {!cancelled && asg.booking_id && (
        <SpotProofButton spotName={asg.spot_name}
          load={() => publicApi.get(`/pointage/public/${token}/booking/${asg.booking_id}/confirmation-proof`).then((r) => r.data)}
          testid={`collab-plan-proof-${asg.booking_id}`} />
      )}
      {!cancelled && rebookAllowed && asg.can_rebook && token && (
        <RebookDialog token={token} bookingId={asg.booking_id} spotName={asg.spot_name} onDone={onRebooked} lock={asg.rebook_lock} />
      )}

      {/* Équipe du jour — repliable pour rester léger */}
      {!cancelled && hasTeam && (
        <div className="mt-2 border-t border-dashed border-[var(--border-soft)] pt-2">
          <button
            type="button"
            onClick={() => setShowTeam((v) => !v)}
            data-testid={`collab-plan-team-toggle-${asg.booking_id}`}
            className="flex w-full items-center justify-between gap-1 text-[10px] font-extrabold uppercase tracking-wide text-[var(--shariti-orange)] transition-colors hover:text-[#c85618]"
          >
            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> Équipe du jour · {team.length}</span>
            {showTeam ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          {showTeam && (
            <div className="mt-2 space-y-1" data-testid={`collab-plan-team-${asg.booking_id}`}>
              {team.map((m, i) => (
                <div key={i} className={`flex items-center gap-2 rounded-lg border px-2 py-1 ${m.is_me ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.06)]" : "border-[var(--border-soft)] bg-[var(--surface-1)]"}`} data-testid={`collab-plan-member-${asg.booking_id}-${i}`}>
                  <RecruiterAvatar name={m.rd_name} src={rdPhotoSrc(m)} size={30} testid={`collab-plan-member-avatar-${asg.booking_id}-${i}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1">
                      <span className="break-words text-[12px] font-bold text-ink">{m.rd_name}</span>
                      {m.is_me && <span className="rounded bg-[var(--shariti-orange)] px-1 text-[8px] font-bold uppercase tracking-wide text-white">Moi</span>}
                      {/* Statut « Indé » VOLONTAIREMENT masqué (vie privée collègues). */}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[10px] text-muted-ink">
                      {m.code_rd && <span className="font-mono font-bold text-[var(--shariti-orange)]">{m.code_rd}</span>}
                      {(m.creneau_debut || m.creneau_fin) && <span className="inline-flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" />{m.creneau_debut || "—"}–{m.creneau_fin || "—"}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CollabPlanning({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (w) => {
    setLoading(true);
    try {
      const q = w ? `?week=${w}` : "";
      const r = await publicApi.get(`/pointage/public/${token}/mon-planning${q}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Planning indisponible.");
      setData(null);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const shiftWeek = (delta) => {
    if (!data?.monday) return;
    const d = new Date(data.monday + "T12:00:00");
    d.setDate(d.getDate() + delta * 7);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    load(`${y}-${m}-${day}`);
  };

  const wd = (iso, opt) => new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", opt);
  const days = data?.days || [];
  const weekRange = days.length
    ? `${wd(days[0].date, { day: "numeric", month: "long" })} – ${wd(days[days.length - 1].date, { day: "numeric", month: "long", year: "numeric" })}`
    : "";

  return (
    <div data-testid="collab-planning">
      <div className="mb-3 flex items-center justify-between gap-2 rounded-2xl border border-[var(--border-soft)] bg-white p-2 shadow-[var(--shadow-sm)]">
        <button onClick={() => shiftWeek(-1)} disabled={loading} data-testid="collab-plan-prev"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-muted-ink transition-colors hover:bg-[var(--surface-2)] disabled:opacity-50"><ChevronLeft className="h-4 w-4" /></button>
        <div className="min-w-0 flex-1 text-center">
          <div className="text-sm font-extrabold text-ink" data-testid="collab-plan-week">Semaine {data?.week_number || "—"}</div>
          <div className="text-[11px] leading-tight text-muted-ink" data-testid="collab-plan-week-range">{weekRange}</div>
        </div>
        <button onClick={() => shiftWeek(1)} disabled={loading || data?.can_navigate_forward === false} data-testid="collab-plan-next"
          title={data?.can_navigate_forward === false ? "Les semaines à venir sont gérées par votre responsable d'équipe." : "Semaine suivante"}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-muted-ink transition-colors hover:bg-[var(--surface-2)] disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight className="h-4 w-4" /></button>
        <button onClick={() => load()} disabled={loading} data-testid="collab-plan-today"
          className="shrink-0 rounded-lg px-2 py-1.5 text-xs font-bold text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.08)] disabled:opacity-50">Cette semaine</button>
      </div>

      {loading ? (
        <div className="space-y-3"><Skeleton className="h-40 w-full rounded-2xl" /></div>
      ) : data?.no_chef ? (
        <div className="rounded-2xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-8 text-center" data-testid="collab-plan-nochef">
          <Users className="mx-auto mb-2 h-8 w-8 text-muted-ink" />
          <h2 className="text-base font-extrabold text-ink">Aucune équipe rattachée</h2>
          <p className="mt-1 text-sm text-muted-ink">Vous n'êtes pas encore rattaché à un chef d'équipe. Contactez votre responsable pour voir votre planning ici.</p>
        </div>
      ) : !days.length ? (
        <div className="rounded-2xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-8 text-center" data-testid="collab-plan-empty">
          <CalendarRange className="mx-auto mb-2 h-8 w-8 text-muted-ink" />
          <h2 className="text-base font-extrabold text-ink">Aucune semaine à afficher</h2>
          <p className="mt-1 text-sm text-muted-ink">Utilisez les flèches pour changer de semaine.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="collab-plan-list">
          {days.map((day) => {
            const assignments = day.assignments || [];
            const isRepos = day.source === "repos" || assignments.length === 0;
            // Date COMPLÈTE et lisible : « Lundi 14 septembre 2026 » (jour + n° + mois entier
            // + année), sans abréviation ni troncature. Seule la 1re lettre est en majuscule
            // (convention FR : mois et jour en minuscules).
            const _fd = wd(day.date, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
            const fullDate = _fd.charAt(0).toUpperCase() + _fd.slice(1);
            return (
              <div
                key={day.date}
                className={`overflow-hidden rounded-2xl border bg-white shadow-[var(--shadow-sm)] ${day.is_holiday ? "border-[rgba(252,219,83,0.65)]" : "border-[var(--border-soft)]"}`}
                data-testid={`collab-plan-day-${day.date}`}
              >
                {/* En-tête du jour */}
                <div className={`flex items-center justify-between gap-2 border-b border-[var(--border-soft)] px-3 py-2 ${day.is_holiday ? "bg-[rgba(252,219,83,0.14)]" : day.is_sunday ? "bg-[rgba(238,114,46,0.05)]" : "bg-[var(--surface-1)]"}`}>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-sm font-extrabold text-ink" data-testid={`collab-plan-day-label-${day.date}`}>{fullDate}</span>
                  </div>
                  {day.is_holiday && (
                    <span className="rounded-full bg-[rgba(252,219,83,0.35)] px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-[#8a6600]">{day.holiday_name || "Férié"}</span>
                  )}
                </div>

                {/* Contenu du jour */}
                <div className="p-2.5">
                  {isRepos ? (
                    <div className="flex items-center gap-2 rounded-xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-3 text-[12px] font-semibold text-muted-ink" data-testid={`collab-plan-repos-${day.date}`}>
                      <CalendarX2 className="h-4 w-4 shrink-0" /> Aucune affectation — repos
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {assignments.map((asg) => (
                        <PlanStoreCard key={asg.booking_id} asg={asg}
                          token={token} rdName={data?.rd_name} onRebooked={() => load(data?.monday)}
                          rebookAllowed={data?.rebook_allowed} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-5 text-center text-[11px] leading-relaxed text-muted-ink" data-testid="collab-plan-legal">
        Vue <b>jour par jour</b> : chaque journée affiche <b>uniquement le magasin où vous devez vous rendre</b>. En cas de renfort, votre lieu habituel est automatiquement remplacé. Lecture seule — toute mise à jour de l'administration s'affiche ici.
      </p>
    </div>
  );
}
